import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert, LinearProgress,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  TrendingUp as PromoteIcon,
  Replay as RepeatIcon,
  ExitToApp as DropoutIcon,
  CompareArrows as TransitionIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, GradeFlowData } from '../../services/learningAnalyticsApi';

const COLORS = {
  promoted: '#388e3c',
  repeated: '#f57c00',
  dropped_out: '#d32f2f',
};

const GradeFlowTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<GradeFlowData | null>(null);
  const [year, setYear] = useState<string>('');
  const [level, setLevel] = useState<string>('1'); // Default to Primary
  const [district, setDistrict] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (level) params.level = parseInt(level);
      if (district) params.district = district;

      const response = await learningAnalyticsAPI.getGradeFlow(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load grade flow data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, level, district]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, grade_metrics, transitions } = data;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Base Year" value={year}
              onChange={(e) => setYear(e.target.value)}
              helperText="Tracks flow from this year to the next">
              <MenuItem value="">Latest Available</MenuItem>
              {[2025, 2024, 2023, 2022, 2021].map(y => (
                <MenuItem key={y} value={y}>{y} → {y + 1}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Education Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Year info banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Showing student flow for <strong>{data.year} → {data.year + 1}</strong> ({data.level}).
        Flow analysis compares enrollment between consecutive years to track student progression.
      </Alert>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PromoteIcon sx={{ color: COLORS.promoted }} />
                <Typography variant="body2" color="textSecondary">Avg Promotion Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {summary.avg_promotion_rate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <RepeatIcon sx={{ color: COLORS.repeated }} />
                <Typography variant="body2" color="textSecondary">Avg Repetition Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: COLORS.repeated }}>
                {summary.avg_repetition_rate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DropoutIcon sx={{ color: COLORS.dropped_out }} />
                <Typography variant="body2" color="textSecondary">Avg Dropout Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {summary.avg_dropout_rate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TransitionIcon color="info" />
                <Typography variant="body2" color="textSecondary">Bottleneck Grade</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                {summary.bottleneck_grade || '-'}
              </Typography>
              {summary.bottleneck_dropout_rate > 0 && (
                <Typography variant="caption" color="error">
                  {summary.bottleneck_dropout_rate}% dropout
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Stacked Bar Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Grade-by-Grade Student Flow</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Showing how students at each grade are distributed: promoted, repeated, or dropped out
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={grade_metrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="grade_label" />
                  <YAxis />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      const labels: Record<string, string> = {
                        promoted: 'Promoted',
                        repeated: 'Repeated',
                        dropped_out: 'Dropped Out',
                      };
                      return [value.toLocaleString(), labels[name] || name];
                    }}
                  />
                  <Legend
                    formatter={(value: string) => {
                      const labels: Record<string, string> = {
                        promoted: 'Promoted',
                        repeated: 'Repeated',
                        dropped_out: 'Dropped Out',
                      };
                      return labels[value] || value;
                    }}
                  />
                  <Bar dataKey="promoted" stackId="a" fill={COLORS.promoted} name="promoted" />
                  <Bar dataKey="repeated" stackId="a" fill={COLORS.repeated} name="repeated" />
                  <Bar dataKey="dropped_out" stackId="a" fill={COLORS.dropped_out} name="dropped_out" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Transition Rate Cards */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Transition Rates</Typography>

              {transitions.pre_primary_to_primary && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">Pre-Primary to Primary</Typography>
                  <Typography variant="h3" fontWeight="bold" color="primary.main">
                    {transitions.pre_primary_to_primary.rate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={transitions.pre_primary_to_primary.rate}
                    sx={{ height: 8, borderRadius: 4, my: 1 }}
                    color="primary"
                  />
                  <Typography variant="caption" color="textSecondary">
                    {transitions.pre_primary_to_primary.transitioned.toLocaleString()} of{' '}
                    {transitions.pre_primary_to_primary.completers.toLocaleString()} completers
                  </Typography>
                </Box>
              )}

              {transitions.primary_to_secondary && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Primary to Secondary</Typography>
                  <Typography variant="h3" fontWeight="bold"
                    color={transitions.primary_to_secondary.rate >= 70 ? 'success.main' : 'warning.main'}>
                    {transitions.primary_to_secondary.rate}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={transitions.primary_to_secondary.rate}
                    sx={{ height: 8, borderRadius: 4, my: 1 }}
                    color={transitions.primary_to_secondary.rate >= 70 ? 'success' : 'warning'}
                  />
                  <Typography variant="caption" color="textSecondary">
                    {transitions.primary_to_secondary.transitioned.toLocaleString()} of{' '}
                    {transitions.primary_to_secondary.completers.toLocaleString()} completers
                  </Typography>
                </Box>
              )}

              {!transitions.pre_primary_to_primary && !transitions.primary_to_secondary && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No transition data available. Requires at least 2 years of enrollment data.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Metrics Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Detailed Grade Metrics</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Grade</TableCell>
                  <TableCell align="right">Total Students</TableCell>
                  <TableCell align="right">Promoted</TableCell>
                  <TableCell align="right">Promotion %</TableCell>
                  <TableCell align="right">Repeated</TableCell>
                  <TableCell align="right">Repetition %</TableCell>
                  <TableCell align="right">Dropped Out</TableCell>
                  <TableCell align="right">Dropout %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grade_metrics.map((g) => (
                  <TableRow key={g.grade_code} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{g.grade_label}</Typography>
                    </TableCell>
                    <TableCell align="right">{g.total_students.toLocaleString()}</TableCell>
                    <TableCell align="right" sx={{ color: COLORS.promoted }}>
                      {g.promoted.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={`${g.promotion_rate}%`} size="small" color="success" variant="outlined" />
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLORS.repeated }}>
                      {g.repeated.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={`${g.repetition_rate}%`} size="small"
                        color={g.repetition_rate > 10 ? 'warning' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLORS.dropped_out }}>
                      {g.dropped_out.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={`${g.dropout_rate}%`} size="small"
                        color={g.dropout_rate > 10 ? 'error' : g.dropout_rate > 5 ? 'warning' : 'default'}
                        variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GradeFlowTab;
